import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  InputAdornment,
  Card,
  CardContent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Inventory as InventoryIcon,
  ShoppingCart as SaleIcon,
  SwapHoriz as TransferIcon,
  Build as AdjustmentIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatCurrency } from '../utils/currency';
import { useGetProductMovementsQuery } from '../services/products.service';


const RastreoPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [shouldSearch, setShouldSearch] = useState(false);

  // Use the API query
  const { data: apiResponse, isLoading, error: apiError, refetch } = useGetProductMovementsQuery(
    { search: searchTerm },
    { skip: !shouldSearch || !searchTerm.trim() }
  );

  const productInfo = apiResponse?.product;
  const movements = apiResponse?.movements || [];
  const error = apiError ? 'Error al buscar el producto. Intente nuevamente.' :
                (apiResponse && !apiResponse.success ? apiResponse.message : null);

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      return;
    }
    setShouldSearch(true);
    // RTK Query will automatically re-run the query when shouldSearch changes
  };

  const handleClear = () => {
    setSearchTerm('');
    setShouldSearch(false);
  };

  const handleRefresh = () => {
    if (shouldSearch && searchTerm.trim()) {
      // Force the query to re-run by toggling shouldSearch
      setShouldSearch(false);
      setTimeout(() => setShouldSearch(true), 10);
    }
  };

  const getMovementTypeColor = (type: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    switch (type) {
      case 'SALE': return 'error';
      case 'TRANSFER_IN': return 'success';
      case 'TRANSFER_OUT': return 'warning';
      case 'ADJUSTMENT': return 'info';
      case 'PURCHASE': return 'success';
      case 'INITIAL_STOCK': return 'default';
      default: return 'default';
    }
  };

  const getMovementTypeLabel = (type: string): string => {
    switch (type) {
      case 'SALE': return 'Venta';
      case 'TRANSFER_IN': return 'Entrada por Transferencia';
      case 'TRANSFER_OUT': return 'Salida por Transferencia';
      case 'ADJUSTMENT': return 'Ajuste de Inventario';
      case 'PURCHASE': return 'Compra';
      case 'INITIAL_STOCK': return 'Stock Inicial';
      default: return type;
    }
  };

  const getMovementIcon = (type: string) => {
    switch (type) {
      case 'SALE': return <SaleIcon fontSize="small" />;
      case 'TRANSFER_IN':
      case 'TRANSFER_OUT': return <TransferIcon fontSize="small" />;
      case 'ADJUSTMENT': return <AdjustmentIcon fontSize="small" />;
      case 'PURCHASE': return <InventoryIcon fontSize="small" />;
      default: return <InventoryIcon fontSize="small" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InventoryIcon />
        Rastreo de Productos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Rastree el historial de movimientos de cualquier producto usando código de barras, SKU, nombre o código alternativo
      </Typography>

      {/* Search Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              fullWidth
              label="Buscar producto"
              placeholder="Ingrese código de barras, SKU, nombre o código alternativo"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : <SearchIcon />}
            >
              {isLoading ? 'Buscando...' : 'Buscar'}
            </Button>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleClear}
              startIcon={<ClearIcon />}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Product Information */}
      {productInfo && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Información del Producto
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Nombre:</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>{productInfo.name}</Typography>
                <Typography variant="subtitle2" color="text.secondary">SKU:</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>{productInfo.sku}</Typography>
                <Typography variant="subtitle2" color="text.secondary">Código de Barras:</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>{productInfo.barcode}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Categoría:</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>{productInfo.category}</Typography>
                <Typography variant="subtitle2" color="text.secondary">Precio de Venta:</Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>{formatCurrency(productInfo.salePrice)}</Typography>
                <Typography variant="subtitle2" color="text.secondary">Stock Actual:</Typography>
                <Typography variant="body1" color={productInfo.currentStock > 0 ? 'success.main' : 'error.main'}>
                  {productInfo.currentStock} {productInfo.unit}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Movement History */}
      {movements.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Historial de Movimientos ({movements.length})
            </Typography>
            <Tooltip title="Actualizar">
              <IconButton onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <TableContainer sx={{ maxHeight: 'calc(100vh - 500px)', overflow: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Fecha/Hora</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Cantidad</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Precio Unit.</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Usuario</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Ubicación</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Observaciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.map((movement) => (
                  <TableRow key={movement.id} hover>
                    <TableCell>
                      {format(new Date(movement.createdAt), 'dd/MM/yyyy HH:mm:ss', { locale: es })}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getMovementIcon(movement.type)}
                        label={getMovementTypeLabel(movement.type)}
                        color={getMovementTypeColor(movement.type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        color={movement.quantity > 0 ? 'success.main' : 'error.main'}
                        fontWeight="bold"
                      >
                        {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {movement.unitPrice ? formatCurrency(movement.unitPrice) : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {movement.total ? formatCurrency(movement.total) : '-'}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{movement.user.name}</Typography>
                        {movement.user.employeeCode && (
                          <Typography variant="caption" color="text.secondary">
                            {movement.user.employeeCode}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{movement.location.name}</Typography>
                        {movement.relatedLocation && (
                          <Typography variant="caption" color="text.secondary">
                            ← {movement.relatedLocation.name}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {movement.reason && (
                          <Typography variant="body2">{movement.reason}</Typography>
                        )}
                        {movement.referenceId && (
                          <Typography variant="caption" color="text.secondary">
                            Ref: {movement.referenceId}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* No results message */}
      {!isLoading && searchTerm && !productInfo && !error && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No se encontraron resultados
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Verifique el código de barras, SKU o nombre del producto e intente nuevamente
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default RastreoPage;